import React, { Ref, useCallback, useContext, useEffect, useImperativeHandle, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View, FlatList, useWindowDimensions, StyleProp, ViewProps } from 'react-native';
import * as AppUtils from '@/utilities/AppUtils';
import * as AppUtilities from '@/utilities/AppUtilities';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import { Image, ImageSource } from 'expo-image';
import {
  ArrowIcon,
  Button,
  Panel,
} from 'react95-native-rabbl';
import * as AppConstants from '@/utilities/AppConstants';
import * as AppConsts from '@/utilities/AppConsts';
import {
  GestureTrigger,
  GestureViewer,
  useGestureViewerController,
  useGestureViewerEvent,
  useGestureViewerState,
} from 'react-native-gesture-image-viewer';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CloseIcon } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';

const styles = StyleSheet.create({
  image: {
    width: '100%',
    height: 200,
  },
  centerAndPadding: {
    padding: 15,
    alignItems: 'center',
  },
  windowView: {
    alignItems: 'center',
  },
  horizontal: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  contentWrapper: {
    padding: 20,
    gap: 20,
    width: '100%',
    alignItems: 'center',
  },
  contentWrapper2: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
    paddingTop: 5,
    gap: 10,
    width: '100%',
  },
  black: {
    color: '#000000',
  },
  divider1: {
    marginTop: 15,
    marginBottom: 10,
  },
  padding5: {
    padding: 5,
  },
  bold: {
    fontWeight: "bold",
  },
  strikethrough: {
    textDecorationLine: 'line-through',
  },
  empty: {
  },
  panelWithTopMargin: {
    marginTop: 7,
    maxWidth: "85%",
    minWidth: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  panel: {
    padding: 20,
    gap: 20,
  },
  wrapper: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 50,
    justifyContent: 'center',
  },
  buttonWrapper: {
    gap: 16,
  },
  galleryContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  thumb: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
    height: AppConsts.BASIC_MINIMUM_WIDTH,
    borderRadius: 8,
    overflow: 'hidden',
  },
  thumbImage: {
    width: '100%',
    height: '100%',
  },
  text: {
    textAlign: 'center',
    color: '#222',
    fontSize: 22,
    fontWeight: 'bold',
  },
  noteText: {
    textAlign: 'center',
    color: 'white',
    paddingHorizontal: 24,
  },
  textWrapper: {
    backgroundColor: AppConsts.COLOR2,
    borderWidth: 1,
    alignSelf: 'flex-start',
    alignItems: 'center',
    padding: 10,
    marginLeft: 20,
  },
  full: {
    width: '100%',
    height: '100%',
  },
});

export interface IGallery {
  open: (index: number) => void;
}

export interface GalleryItem {
  source: ImageSource,
  note: string,
  key: string,
}

type Props = {
  ref?: Ref<IGallery>,
  items: GalleryItem[],
  descriptions?: boolean,
};

const CLOSE_ICON_WRAPPER_SIZE = 30;
const FLOATING_PADDING = 20;

// https://github.com/saseungmin/react-native-gesture-image-viewer
// https://react-native-gesture-image-viewer.pages.dev/guide/getting-started/quick-start.html
// https://snack.expo.dev/@harang/react-native-gesture-image-viewer-v2
export function Gallery({ items, ref, descriptions = false } : Props) {
  const [visible, setVisible] = useState(false);
  const [enableLoop, setEnableLoop] = useState(false);
  const [showExternalUI, setShowExternalUI] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { width, height } = useWindowDimensions();

  const { goToIndex, goToPrevious, goToNext, zoomIn, zoomOut, resetZoom, rotate } =
    useGestureViewerController();

  const { currentIndex, totalCount } = useGestureViewerState();

  const insets = useSafeAreaInsets();

  const modalOpen = useCallback((index: number) => {
    setSelectedIndex(index);
    setShowExternalUI(true);
    setVisible(true);
  }, []);

  const renderImage = useCallback((source: ImageSource) => {
    return (
      <Image
        source={source}
        style={styles.full}
        contentFit="contain"
      />
    );
  }, []);

  useImperativeHandle(ref, () => ({
    open: (index: number) => {
      modalOpen(index);
    },
  }), [modalOpen]);

  return (
    <Modal
      visible={visible}
      onRequestClose={() => setVisible(false)}
      animationType="none"
    >
      <View style={{ flex: 1, width: width, height: height }}>
        <GestureViewer
          data={items.map(({ source }) => source)}
          initialIndex={selectedIndex}
          onDismiss={() => setVisible(false)}
          onDismissStart={() => setShowExternalUI(false)}
          enableLoop={enableLoop}
          ListComponent={FlatList}
          renderItem={renderImage}
          dismiss={{
            direction: 'both',
          }}
          onSingleTap={() => setShowExternalUI((prev) => !prev)}
          backdropStyle={{ backgroundColor: '#181818' }}
          renderContainer={(children, helpers) => (
            <View style={{ flex: 1, width: width, height: height }}>
              {children}
              {showExternalUI && (
                <View
                  style={{
                    position: 'absolute',
                    top: insets.top + FLOATING_PADDING,
                    right: FLOATING_PADDING,
                    zIndex: 1000,
                    width: CLOSE_ICON_WRAPPER_SIZE,
                    height: CLOSE_ICON_WRAPPER_SIZE,
                  }}
                >
                  <Button onPress={helpers.dismiss}>
                    <CloseIcon />
                  </Button>
                </View>
              )}
            </View>
          )}
        />
        {showExternalUI && (
          <>
            <View
              style={
                AppUtilities.isWeb() ?
                    {
                      position: "absolute",
                      left: 0,
                      justifyContent: "flex-end",
                      gap: 10,
                      zIndex: 999,
                      width: width,
                      height: 100,
                      top: height - 100 - FLOATING_PADDING,
                    }
                  :
                    {
                      position: "absolute",
                      bottom: insets.bottom + FLOATING_PADDING,
                      left: 0,
                      justifyContent: "flex-end",
                      gap: 10,
                      zIndex: 999,
                      width: width,
                    }
              }
            >
              <View
                style={{
                  flexDirection: 'row',
                  gap: 10,
                  justifyContent: 'space-around',
                  alignItems: 'center',
                  width: "100%",
                }}
              >
                <Button
                  onPress={goToPrevious}
                  disabled={currentIndex == 0}
                >
                  <ArrowIcon
                    segments={4}
                    direction='left'
                    disabled={currentIndex == 0}
                  />
                </Button>
                <Button
                  onPress={currentIndex == items.length - 1 ? () => setVisible(false) : goToNext}
                >
                  <ArrowIcon
                    segments={4}
                    direction='right'
                  />
                </Button>
              </View>
              {
                descriptions && (
                  <View style={styles.textWrapper}>
                    <AppText style={CustomTextStyles.tiny} numberOfLines={1} ellipsizeMode='tail'>
                      {`${currentIndex + 1} / ${totalCount}`}
                    </AppText>
                    <AppText style={CustomTextStyles.tiny} numberOfLines={1} ellipsizeMode='tail'>
                      {items[currentIndex]?.note}
                    </AppText>
                  </View>
                )
              }
            </View>
          </>
        )}
      </View>
    </Modal>
  );
}
